import sendEventToIntercom from '../../services/IntercomEventService';
import isVisualSearchEnabled from '../isVisualSearchEnabled';
import { isUnsubscribed } from '../../store/helpers/user';
import { decodeSlash } from '../../shared/utils';

export const EVENT_NAME = 'search_empty_state_trigged';
// Structural/view-state URL params that don't represent a search filter.
// Mirrors the denylist used by `isRouteFiltering` (helpers/history.js) so any
// real filter (params2, assignees, commentedBy, approvedBy, meta.*, etc.)
// counts, while `text` (tracked separately as the query) and geo view params
// (bbox/zoom, added automatically in geo mode) don't create false positives.
export const NON_SEARCH_FILTER_KEYS = ['collectionIds', 'lightboardId', 'recursive', 'trashed', 'text', 'bbox', 'zoom'];
export const REQUIRED_EMPTY_STATES = 3;
export const EMPTY_STATE_WINDOW_MS = 30 * 60 * 1000;

/**
 * In-memory tracker of the current consecutive search-empty-state streak.
 * A streak is broken (reset) by any successful search or by switching user.
 * `triggered` guards against sending duplicate events for the same streak.
 */
let tracker = { userId: null, timestamps: [], triggered: false };

function resetTracker(userId = null) {
  tracker = { userId, timestamps: [], triggered: false };
}

/**
 * Paid user = has an active subscription plan and is not on a trial/free plan.
 * Trial and unsubscribed/overdue users must never trigger the event.
 */
export function isPaidUser(user) {
  if (!user || !user.subscriptionFeatures) return false;
  if (user.subscriptionFeatures.planId === 'trial') return false;
  return !isUnsubscribed(user);
}

/** Exposed for tests to reset the module-level streak between cases. */
export function resetSearchEmptyStateTracker() {
  resetTracker(null);
}

/**
 * Returns search filter keys present in URL search props (used by getAssets).
 * Any key that isn't a structural/view-state param counts as an applied filter.
 */
export function getAppliedSearchFilters(searchProps = {}) {
  return Object.keys(searchProps).filter(
    (key) => !NON_SEARCH_FILTER_KEYS.includes(key),
  );
}

/**
 * Builds trackSearchEmptyState params from getAssets fetch context.
 */
export function buildSearchEmptyStateTrackingParams({
  text,
  searchProps,
  total,
  user,
  activeCollection,
  source,
} = {}) {
  const appliedFilters = getAppliedSearchFilters(searchProps);

  return {
    user,
    isSearch: Boolean(text) || appliedFilters.length > 0,
    isEmpty: total === 0,
    searchQuery: text || '',
    filtersApplied: appliedFilters.join(', '),
    collectionId: activeCollection?._id || '',
    collectionName: activeCollection?.name ? decodeSlash(activeCollection.name) : '',
    source,
  };
}

/**
 * Tracks the search empty state and sends the `search_empty_state_trigged`
 * Intercom event once a paid user hits the empty state 3 times in a row
 * within a 30-minute window.
 *
 * @param {Object} params
 * @param {Object} params.user - current user
 * @param {boolean} params.isSearch - whether an actual search query was performed
 * @param {boolean} params.isEmpty - whether the search returned no assets
 * @param {string} [params.searchQuery] - text query used for the search
 * @param {string} [params.filtersApplied] - human readable list of applied filters
 * @param {string} [params.collectionId] - active collection id
 * @param {string} [params.collectionName] - active collection name
 * @param {string} [params.source] - product surface the search originated from
 * @returns {boolean} true when the Intercom event was sent
 */
export default function trackSearchEmptyState({
  user,
  isSearch,
  isEmpty,
  searchQuery = '',
  filtersApplied = '',
  collectionId = '',
  collectionName = '',
  source = '',
} = {}) {
  if (!user) return false;

  const userId = String(user._id || '');
  if (tracker.userId !== userId) resetTracker(userId);

  // Only consecutive searches count. Plain collection browsing is ignored.
  if (!isSearch) return false;

  // A successful search result breaks the consecutive empty-state streak.
  if (!isEmpty) {
    resetTracker(userId);
    return false;
  }

  // Trial/free users never trigger this event.
  if (!isPaidUser(user)) return false;

  const now = Date.now();
  tracker.timestamps.push(now);
  // Keep only the empty states that fall inside the 30-minute window.
  tracker.timestamps = tracker.timestamps.filter((timestamp) => now - timestamp <= EMPTY_STATE_WINDOW_MS);

  if (tracker.timestamps.length < REQUIRED_EMPTY_STATES) return false;

  // The same repeated-search-failure streak must not trigger duplicate events.
  if (tracker.triggered) return false;
  tracker.triggered = true;

  sendEventToIntercom(EVENT_NAME, {
    userId,
    team: String(user.team?._id || user.teamId || ''),
    plan_name: user.subscriptionFeatures?.planName,
    search_query: searchQuery,
    filters_applied: filtersApplied,
    collectionId,
    collectionName,
    empty_state_count: tracker.timestamps.length,
    has_visual_search_enabled: isVisualSearchEnabled(user),
    source,
  });

  return true;
}
